import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Save, Upload, Trash2, Crop } from 'lucide-react';
import { footerApi } from '../../utils/api';
import { refreshSiteSettings } from '../../hooks/useSiteSettings';
import RectCropModal from '../common/RectCropModal';

const LOGO_SLOTS = [
  { key: 'header_logo', field: 'headerLogo', label: 'हेडर लोगो', hint: 'मुख्य हेडर (बायाँतर्फ) — सिफारिस: उच्च चौडाइ, पारदर्शी PNG' },
  { key: 'mininav_logo', field: 'mininavLogo', label: 'मिनी नेभ लोगो', hint: 'स्क्रोल गर्दा देखिने माथिल्लो पट्टी — सानो आकार' },
  { key: 'footer_logo', field: 'footerLogo', label: 'फुटर लोगो', hint: 'फुटरको ब्रान्ड भाग' },
];

function AdminLogos() {
  const [settings, setSettings] = useState({});
  const [files, setFiles] = useState({});
  const [previews, setPreviews] = useState({});
  const [removed, setRemoved] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Header background photo state
  const [bgFile, setBgFile] = useState(null);
  const [bgPreview, setBgPreview] = useState('');
  const [bgRemoved, setBgRemoved] = useState(false);
  const [cropSrc, setCropSrc] = useState(null); // crop modal ma dekhaine image src
  const [pendingCropSrc, setPendingCropSrc] = useState(null); // naya file crop huna baki cha?

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await footerApi.getSettings();
      setSettings(response.data || {});
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (slot, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFiles({ ...files, [slot.field]: file });
    setPreviews({ ...previews, [slot.key]: URL.createObjectURL(file) });
    setRemoved({ ...removed, [slot.key]: false });
  };

  const handleRemove = (slot) => {
    const newFiles = { ...files };
    delete newFiles[slot.field];
    setFiles(newFiles);
    setPreviews({ ...previews, [slot.key]: '' });
    setRemoved({ ...removed, [slot.key]: true });
  };

  // ---- Header background photo ----
  const handleBgSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = (event) => {
      setCropSrc(event.target.result);
      setPendingCropSrc(event.target.result);
      setBgRemoved(false);
    };
    reader.readAsDataURL(file);
  };

  const applyBgCrop = (file) => {
    if (bgPreview) URL.revokeObjectURL(bgPreview);
    const url = URL.createObjectURL(file);
    setBgFile(file);
    setBgPreview(url);
    setCropSrc(null);
    setPendingCropSrc(null);
  };

  const reCropCurrentBg = () => {
    // Naya selected file bhaye tyahi bata, natra saved photo bata crop garna
    setCropSrc(pendingCropSrc || bgPreview || settings.header_bg_photo || '');
  };

  const handleBgRemove = () => {
    if (bgPreview) URL.revokeObjectURL(bgPreview);
    setBgFile(null);
    setBgPreview('');
    setBgRemoved(true);
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.keys(removed).forEach((key) => {
        if (removed[key]) payload[key] = '';
      });
      if (bgRemoved) payload.header_bg_photo = '';
      await footerApi.updateSettings(payload, { ...files, ...(bgFile ? { headerBg: bgFile } : {}) });
      toast.success('लोगोहरू सफलतापूर्वक अद्यावधिक गरियो।');
      await refreshSiteSettings();
      await fetchSettings();
      setFiles({});
      setPreviews({});
      setRemoved({});
      setBgFile(null);
      setBgPreview('');
      setBgRemoved(false);
    } catch (error) {
      console.error('Error updating logos:', error);
      toast.error('अद्यावधिक गर्न असफल भयो। कृपया पुनः प्रयास गर्नुहोस्।');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-16 text-muted">लोड हुँदै...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <h2 className="font-display text-2xl">लोगो अद्यावधिक</h2>
        <button
          className="inline-flex items-center gap-1.5 bg-crimson text-white font-semibold text-[13.5px] px-5 py-2.5 rounded-full hover:bg-crimson-dark disabled:opacity-60"
          onClick={handleSubmit}
          disabled={saving}
        >
          <Save size={15} /> {saving ? 'सुरक्षित गर्दै...' : 'सुरक्षित गर्नुहोस्'}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {LOGO_SLOTS.map((slot) => {
          const current = previews[slot.key] !== undefined ? previews[slot.key] : settings[slot.key] || '';
          return (
            <div key={slot.key} className="bg-white border border-line rounded-[14px] p-5 flex flex-col gap-3">
              <h3 className="font-display text-lg">{slot.label}</h3>
              <div className="h-[110px] bg-paper-soft border border-dashed border-line rounded-xl flex items-center justify-center overflow-hidden p-2">
                {current ? (
                  <img src={current} alt={slot.label} className="max-h-full max-w-full object-contain" />
                ) : (
                  <span className="text-[12px] text-muted">लोगो छनिएको छैन</span>
                )}
              </div>
              <p className="text-[11.5px] text-muted leading-snug">{slot.hint}</p>
              <div className="flex gap-2 mt-auto">
                <label className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-ink-soft border border-line rounded-lg px-3 py-2 cursor-pointer hover:border-crimson hover:text-crimson w-fit">
                  <Upload size={14} /> छान्नुहोस्
                  <input type="file" accept="image/*" hidden onChange={(e) => handleFileChange(slot, e)} />
                </label>
                {current && (
                  <button
                    type="button"
                    onClick={() => handleRemove(slot)}
                    className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-crimson border border-line rounded-lg px-3 py-2 hover:border-crimson"
                  >
                    <Trash2 size={14} /> हटाउनुहोस्
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Header background photo — crop + live preview */}
      <div className="bg-white border border-line rounded-[14px] p-5 md:p-6 mt-4">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
          <div>
            <h3 className="font-display text-lg">हेडर ब्याकग्राउन्ड फोटो</h3>
            <p className="text-[11.5px] text-muted leading-snug">हेडरको पृष्ठभूमिमा देखिने फराकिलो फोटो — ५:१ अनुपातमा क्रप हुन्छ। नराख्दा सेतो रङ।</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {(() => {
              const bgCurrent = bgPreview || (!bgRemoved && settings.header_bg_photo) || '';
              return bgCurrent ? (
                <>
                  <button type="button" onClick={reCropCurrentBg} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-blue border border-line rounded-lg px-3 py-2 hover:border-blue transition-colors">
                    <Crop size={14} /> फेरि क्रप
                  </button>
                  <button type="button" onClick={handleBgRemove} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-crimson border border-line rounded-lg px-3 py-2 hover:border-crimson transition-colors">
                    <Trash2 size={14} /> हटाउनुहोस्
                  </button>
                </>
              ) : null;
            })()}
          </div>
        </div>

        {/* LIVE PREVIEW — photo thapiyeko turuntai header jastai dekhincha */}
        <div className="relative h-[110px] sm:h-[130px] rounded-xl overflow-hidden border border-line mb-3 bg-white">
          {(() => {
            const bgCurrent = bgPreview || (!bgRemoved && settings.header_bg_photo) || '';
            if (bgCurrent) {
              return (
                <>
                  <img src={bgCurrent} alt="Header background preview" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-r from-white/85 via-white/60 to-transparent" />
                </>
              );
            }
            return <div className="absolute inset-0 bg-gradient-to-r from-gray-100 to-white" />;
          })()}
          {/* Mock header content */}
          <div className="relative h-full max-w-[1240px] mx-auto px-5 flex items-center justify-between pointer-events-none">
            <span className="font-display font-bold tracking-tight text-ink text-[22px] sm:text-[26px] drop-shadow-sm">
              KALO<em className="not-italic text-crimson">JHOLA</em>
            </span>
            <span className="hidden sm:flex flex-col items-end text-right text-[11px] font-semibold text-ink/80 drop-shadow-sm leading-snug">
              <span>बिहीबार, भदौ ६, २०८३</span>
              <span>१०:३० बिहान • २८°C</span>
            </span>
          </div>
          {!((bgPreview || (!bgRemoved && settings.header_bg_photo))) && (
            <span className="absolute bottom-2 right-3 text-[10.5px] font-semibold text-muted bg-white/80 rounded-full px-2 py-0.5">लाइभ प्रिभ्यू — फोटो थप्नुहोस्</span>
          )}
          {(bgPreview || bgRemoved) && (
            <span className="absolute top-2 right-3 text-[10px] font-bold text-green-700 bg-green-100 rounded-full px-2 py-0.5">
              {bgRemoved ? 'हटाइयो — सुरक्षित गर्नुहोस्' : 'नयाँ — सुरक्षित गर्नुहोस्'}
            </span>
          )}
        </div>

        <label className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-ink-soft border border-line rounded-lg px-3 py-2 cursor-pointer hover:border-crimson hover:text-crimson w-fit transition-colors">
          <Upload size={14} /> फोटो छान्नुहोस् (क्रप हुन्छ)
          <input type="file" accept="image/*" hidden onChange={handleBgSelect} />
        </label>
      </div>

      {cropSrc && (
        <RectCropModal imageSrc={cropSrc} onCancel={() => setCropSrc(null)} onApply={applyBgCrop} />
      )}
    </div>
  );
}

export default AdminLogos;
